import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Modal from '../components/Modal.tsx';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable';
import { api } from '../lib/api.ts';
import AnchoredPop from '../components/AnchoredPop.tsx';
import { colourHex } from '../lib/format.ts';
import { baseOf, groupBlocks, isDivider, resolveReorder, resolveMultiReorder } from '../lib/slideBlocks.ts';
import { useIframeDragGuard } from '../lib/useIframeDragGuard.ts';
import { useDismissMenu } from '../lib/useDismissMenu.ts';
import { useVisibleSet } from '@shared-client/useVisibleSet.ts';
import { useThumbWarm, thumbKey, type Thumb as WarmThumb } from '@shared-client/useThumbWarm.ts';
import { STRUCTURAL_MODEL, resolveModelChoice } from '../lib/models.ts';
import {
  ArrowRight,
  Drag,
  KebabHorizontal,
  LibraryIcon,
  Merge,
  Plus,
  Refresh,
  Trash,
  ZoomIn,
  X,
} from '../lib/icons.tsx';
import AccentSwatches from './AccentSwatches.tsx';
import ConfirmModal from '../components/ConfirmModal.tsx';
import SlidePreviewModal from '../components/SlidePreviewModal.tsx';
import ZoomLevelControl from '../components/ZoomLevelControl.tsx';
import { usePersistedState } from '../lib/persistedState.ts';
import { cssVars } from '../lib/css.ts';
import type { DragEndEvent } from '@dnd-kit/core';
import type { RefObject } from 'react';
import type { DeckSection, SectionRef, SlideEntry } from '../../shared/deck.ts';
import type { DeckThumbsPayload } from '../../shared/api.ts';
import type { SlideBlock, SectionMove } from '../lib/slideBlocks.ts';
import type { ChatModelOption } from '../lib/models.ts';

/** How freely the AI may edit the words when folding slides together. The
 *  values match the levels the deck system prompt defines. */
type MergeLevel = 'strict' | 'light' | 'condense';

/** What the merge bar hands back — a whole request, not a slide list, because
 *  the level and the model change what the turn is allowed to do. */
export type MergeRequest = {
  /** In DECK order: the first is the slide that survives. */
  slides: SlideEntry[];
  level: MergeLevel;
  instructions: string;
  model: string | null;
};

/** One cached thumbnail, as `GET /api/decks/:slug/thumbs` reports it. */
/** Aliased to the warm hook's declaration, like the gallery's: this grid's
 *  cached thumbs are handed straight to `useThumbWarm` as its `known` map, so
 *  the two shapes have to agree. */
type Thumb = WarmThumb;

/** The two whole-deck verbs the ⋮ menu offers. Separate items on purpose: the
 *  old rule picked between them from `missingThumbs === 0`, which stopped
 *  meaning anything once most slides are deliberately never requested. */
type WholeDeckRun = 'retry' | 'regenerate';

/** Where an arrow key moves the focused tile. */
type Direction = 'left' | 'right' | 'up' | 'down';

// 100% on the thumbnail-size control. Mirrors the grid's minmax floor in
// editor.css — change one and the readout stops meaning "as it always looked".
const OV_TILE_BASE = 200;

/**
 * Whole-deck overview: a grid of live slide thumbnails so the user can see the
 * deck at a glance, jump to any slide, and reshape it — reorder by dragging,
 * recolour/rename a section in place, and act on a selection (delete, merge,
 * split, or save to the RKH library). The filmstrip does all of this in a
 * narrow column; the overview is the same structural surface at deck scale, so
 * it reuses the SAME editor handlers (commitReorder / handleRecolourSection /
 * handleRenameSection / handleDeleteSlides) — the two can't drift.
 *
 * ## The interaction model: click selects, double-click opens
 *
 * ONE meaning per gesture, the slide-sorter idiom (Keynote, PowerPoint,
 * Finder): click selects, Cmd-click adds, Shift-click extends a range,
 * double-click (or Enter) opens the slide and closes the overview, arrow keys
 * walk the grid.
 *
 * Cmd-click is the fast path, not the only one: every tile carries a tick
 * checkbox (top-left, the mirror of the drag grip) that toggles that slide in
 * or out of the selection with a plain click. It's what makes "slide 4 and
 * slide 7" reachable without knowing a modifier — the gesture nobody discovers
 * from a hint (Jake, 2026-07-30). It appears on hover, and stays visible on
 * EVERY tile once something is selected, so building a scattered selection
 * never involves hunting for an affordance. It is not a mode: it's the same
 * toggle Cmd-click performs, just visible.
 *
 * This replaced (2026-07-30) a surface where the same click meant three
 * different things depending on invisible state: navigate-and-close while
 * browsing, tick-a-checkbox inside a 'delete' / 'merge' / 'save' MODE the
 * header verbs pushed you into, and a parallel hover-checkbox selection that
 * each wiped the other. Because click now always means select, those modes are
 * gone: Merge / Save / Delete are plain verbs acting on whatever is selected,
 * shown in a selection bar that replaces the idle header. Nothing is a mode,
 * so nothing has to be cancelled.
 *
 * The cost is one extra click to jump to a slide. It's bought back by Enter,
 * the arrow keys, and the explicit "Open slide N" primary action — all of which
 * beat hunting for a tile with the mouse.
 *
 * Merge is the inverse of the slide menu's "Split into two slides" and works
 * the same way — no deterministic path, it hands a prompt to the deck chat and
 * the user reviews the resulting Apply cards. It needs options (how freely the
 * AI may edit the words), so it opens a footer bar over the selection.
 *
 * ## Tile states
 *
 * Selection owns the loud state (accent ring + tint + tick) because it's what
 * every verb targets. The CURRENT slide gets an accent number chip instead —
 * the two used to be near-identical accent rings, which is why the old code had
 * to hide the active ring while picking.
 *
 * Each tile is a cached server-rendered thumbnail; tiles without one yet show a
 * grey shimmer (no live iframe fallback — N iframes would hammer the deck's
 * Vite child and flash on reorder).
 *
 * Structure mirrors the filmstrip: multi-instance slides (multi: N) share one
 * manifest row and move/delete as one block, so everything operates in block
 * space (groupBlocks), never inside a block. Reordering also mirrors it —
 * dnd-kit, whole-tile drag, an explicit insertion bar rather than shifting
 * tiles (see the Filmstrip header for why). One SortableContext spans EVERY
 * block even though the grid renders a separate <ul> per section run: the
 * destination section is inferred from the drop's neighbour, so a single flat
 * context is what keeps cross-section drops working.
 */
/** How long the overview keeps asking the server for pending thumbnails before
 *  giving up and saying so. A deck with a slide that won't render never empties
 *  `pending`, so without a budget the poll runs for as long as the overview is
 *  open.
 *
 *  This is only the BACKSTOP — `coolingUntil` and the no-progress check below
 *  catch a stuck deck far sooner. So it must sit above the server's own
 *  PASS_TIMEOUT_MS (180s), or the client abandons a big cold deck that the
 *  server is still rendering perfectly well. */
const THUMB_POLL_BUDGET_MS = 240_000;

type DeckOverviewProps = {
  slug: string;
  slides?: SlideEntry[];
  sections?: DeckSection[];
  activeIdx?: number;
  onSelect: (idx: number) => void;
  onClose: () => void;
  /** Absent makes the whole overview read-only — see `editable` below, which
   *  the dashboard relies on rather than passing a flag. */
  onReorder?: ((newList: SlideEntry[], movedSlide: SlideEntry, toSection: string | null) => void) | null;
  onReorderMulti?:
    | ((
        newList: SlideEntry[],
        movedBlocks: SlideBlock[],
        toSection: string | null,
        sectionMoves: SectionMove[],
      ) => void)
    | null;
  /** Hand a split to deck chat. Single-instance slides only. */
  onSplitSlide?: ((slide: SlideEntry) => void) | null;
  onDeleteSlides?: ((slides: SlideEntry[]) => void) | null;
  onMergeSlides?: ((req: MergeRequest) => void) | null;
  /** From `useModelChoice`. Empty hides the merge bar's Model row entirely. */
  modelOptions?: ChatModelOption[] | null;
  /** Copy a selection into the RKH library. */
  onSaveSlides?: ((slides: SlideEntry[]) => void) | null;
  onInsertSlides?: (() => void) | null;
  onRecolourSection?: ((section: SectionRef, token: string) => void) | null;
  onRenameSection?: ((section: SectionRef, name: string) => void) | null;
  /** Open straight into the merge footer (from a slide's ⋮ menu). */
  initialMode?: 'merge' | null;
  /** Pre-select this slide on open. A FULL id — it is reduced to its block
   *  baseId here, since selection is block-space. */
  seedSlideId?: string | null;
  busy?: boolean;
};

export default function DeckOverview({
  slug,
  slides,
  sections,
  activeIdx,
  onSelect,
  onClose,
  onReorder,
  onReorderMulti,
  onSplitSlide,
  onDeleteSlides,
  onMergeSlides,
  modelOptions,
  onSaveSlides,
  onInsertSlides,
  onRecolourSection,
  onRenameSection,
  initialMode,
  seedSlideId,
  busy,
}: DeckOverviewProps) {
  const [query, setQuery] = useState('');
  // Block baseIds throughout.
  const [selected, setSelected] = useState<Set<string>>(() =>
    seedSlideId ? new Set([baseOf(String(seedSlideId))]) : new Set(),
  );
  // The pivot a Shift-click extends from — the last tile clicked without Shift.
  const [anchorId, setAnchorId] = useState<string | null>(() => (seedSlideId ? baseOf(String(seedSlideId)) : null));
  const [mergeOpen, setMergeOpen] = useState(() => initialMode === 'merge');
  const [mergeLevel, setMergeLevel] = useState<MergeLevel>('light');
  const [mergeNotes, setMergeNotes] = useState('');
  // A merge deletes slides, so it starts on the strongest model rather than on
  // the ⚙ default — see STRUCTURAL_MODEL. The user can move off it; the row is
  // seeded lazily and only re-seeded while they haven't, so a settings refresh
  // can't yank the pick back mid-decision.
  const [mergeModel, setMergeModel] = useState<string | null>(null);
  const mergeModelPicked = useRef(false);
  const modelChoice = mergeModelPicked.current ? mergeModel : resolveModelChoice(modelOptions, STRUCTURAL_MODEL);
  const pickMergeModel = (m: string) => {
    mergeModelPicked.current = true;
    setMergeModel(m);
  };
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  // dnd-kit drag state — base ids of the dragged block and the one it's over.
  // Strings, not dnd-kit's UniqueIdentifier: every id here is a baseId we
  // supplied, and `dragIdSet` feeds resolveMultiReorder, which wants strings.
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  // Cached slide thumbnails (id -> { url }); tiles without one yet show a grey
  // ghost skeleton. Reorder reshuffles cached <img>s with no reload/flash.
  const [thumbs, setThumbs] = useState<Record<string, Thumb>>({});
  const [regenerating, setRegenerating] = useState(false);
  // Set when thumbnail generation gives up, so the grey tiles are explained
  // rather than just sitting there.
  const [thumbNote, setThumbNote] = useState<string | null>(null);
  // The slide open in the zoom modal (a live frame), or null. Tiles are 640x360
  // rasters, so "look closer" can't just scale one up.
  const [zoomSlide, setZoomSlide] = useState<SlideEntry | null>(null);
  // How big the tiles are drawn, as a percentage of the grid's base width.
  // Persisted: a big-screen user who wants 200% wants it every time. The cap is
  // 300% (600px), which is where a 640x360 thumbnail runs out on a 1x display;
  // past that, and on a 2x one from about 200%, the tile is upscaled. Left where
  // it is deliberately — this is a sorting view, and the magnifier below is the
  // answer to "I need to READ this slide".
  const [tileZoom, setTileZoom] = usePersistedState('studio.overviewTileZoom', 100);
  // Bumped to force a re-poll (after a regenerate).
  const [reloadTick, setReloadTick] = useState(0);
  // Slides the server has given up on, from the last read.
  const [givenUp, setGivenUp] = useState<string[]>([]);
  // True while a WHOLE-DECK pass the user asked for is running, which is the
  // only thing left worth polling the deck-wide count for.
  const wholeDeckRun = useRef(false);
  const restoreFocus = useRef<HTMLElement | null>(null);
  const activeTileRef = useRef<HTMLButtonElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const editable = Boolean(onReorder && onDeleteSlides);
  const hasSelection = selected.size > 0;

  // Read what is already cached, once — then let the viewport ask for the rest.
  //
  // This used to be a GENERATING poll: opening the overview rendered every slide
  // in the deck, all 120 of them for a grid showing 18. The tiles the user can
  // see are now requested by the observer below, and a slide never scrolled to
  // is never rendered.
  //
  // Still polls after a REBUILD, because that is a whole-deck pass the user
  // explicitly asked for and the tiles should fill in as it goes.
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const deadline = Date.now() + THUMB_POLL_BUDGET_MS;
    let lastLeft: number | null = null;
    let progressedAt = Date.now();
    const NO_PROGRESS_MS = 75_000;
    async function poll() {
      try {
        // Partial: every field below is already defended with `|| …`, which is
        // the honest shape for something polled while it is still being built.
        const res = await api.get<Partial<DeckThumbsPayload>>(`/api/decks/${slug}/thumbs?generate=0`);
        if (cancelled) return;
        setThumbs(res.thumbs || {});
        setGivenUp(res.givenUp || []);
        // Nothing of ours is running: this is a snapshot, not a wait.
        if (!wholeDeckRun.current) return;
        const left = (res.pending || []).length;
        if (!left) {
          setThumbNote(null);
          setRegenerating(false);
          wholeDeckRun.current = false;
          return;
        }
        // ANY change is progress, not just a decrease. A rebuild makes the count
        // jump UP, which is new work appearing — treating that as "no progress"
        // burned the whole stall budget during a rebuild and then declared a
        // perfectly healthy deck unrenderable. A deck genuinely stuck holds the
        // count constant, so the detector still catches it.
        if (left !== lastLeft) {
          lastLeft = left;
          progressedAt = Date.now();
        }
        const stalled =
          (res.coolingUntil && res.coolingUntil > Date.now()) ||
          Date.now() - progressedAt > NO_PROGRESS_MS ||
          Date.now() > deadline;
        if (!stalled) {
          timer = setTimeout(poll, 800);
          return;
        }
        setRegenerating(false);
        wholeDeckRun.current = false;
        setThumbNote("Some thumbnails couldn't be rendered. Try Refresh thumbnails, or open the slides to check them.");
      } catch {
        // Leave the cached tiles as they are — but never leave "Refresh
        // thumbnails" stuck on its disabled "Refreshing…" state, or the only
        // way back is closing and reopening the overview.
        if (cancelled) return;
        setRegenerating(false);
        wholeDeckRun.current = false;
        setThumbNote('Lost contact with the server while loading thumbnails. Try Refresh thumbnails.');
      }
    }
    poll();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [slug, reloadTick]);

  // The viewport decides what gets rendered. Tiles register themselves through
  // `observe` below; scrolling to one IS the request for its image.
  const { visible, observe } = useVisibleSet();
  const {
    thumbs: warmed,
    status: warmStatus,
    coming,
    isStuck,
    retry: retryThumb,
  } = useThumbWarm({ visible, known: { [slug]: thumbs } });
  const thumbFor = (id: string): Thumb | undefined => warmed[slug]?.[id] || thumbs[id];
  // Slides the SERVER has stopped trying to render — the honest source of a
  // tile's "failed" state, and the only thing the note should ever explain. A
  // grey tile for a slide nobody has scrolled to is not an error.
  const failed = new Set([...(givenUp || []), ...(warmStatus[slug]?.givenUp || [])]);

  /** Render the whole deck now, or rebuild it — both explicit, both whole-deck.
   *
   *  Automatic generation follows the viewport; asking for everything is still
   *  allowed, it just has to be asked for. The old rule picked between retry and
   *  rebuild from `missingThumbs === 0`, which stopped meaning anything the
   *  moment most slides are deliberately never requested: missing no longer
   *  implies attempted, so that test was permanently false and "rebuild" became
   *  unreachable. The two verbs are now separate menu items.
   *
   *  `retry` also lifts the server's backoff — deck-wide AND the per-slide
   *  give-ups — which is exactly what "try the failed ones again" has to do. */
  async function runWholeDeck(mode: WholeDeckRun) {
    setMoreOpen(false);
    setRegenerating(true);
    setThumbNote(null);
    // Only a rebuild invalidates what is on screen; a retry leaves every good
    // tile exactly where it is.
    if (mode === 'regenerate') setThumbs({});
    wholeDeckRun.current = true;
    try {
      await api.post(`/api/decks/${slug}/thumbs/${mode}`);
    } catch {
      /* re-poll anyway */
    }
    setReloadTick((t) => t + 1);
  }

  // Restore focus to whatever opened the overview when it closes.
  useEffect(() => {
    // Only an HTMLElement can be focused back; the old `?.focus?.()` guard was
    // making the same check one step later.
    restoreFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    return () => {
      try {
        restoreFocus.current?.focus();
      } catch {
        /* element gone — nothing to restore */
      }
    };
  }, []);

  // Scroll the active slide into view on open (a long deck opens deep).
  useEffect(() => {
    activeTileRef.current?.scrollIntoView({ block: 'center' });
  }, []);

  const sectionByKey = useMemo(() => {
    const map = new Map((sections || []).map((s) => [s.key, s]));
    return (key: string): SectionRef => map.get(key) || { key, name: key, color: null };
  }, [sections]);

  // Consecutive slides sharing a file form one draggable block; contiguous
  // same-section blocks form a section run (kept together under one heading).
  const blocks = useMemo(() => groupBlocks(slides), [slides]);
  // The zoom modal walks the whole deck with ← →. Memoised: the array feeds a
  // keydown effect in there, so a fresh identity per render would re-subscribe.
  const zoomIdxs = useMemo(() => (slides || []).map((s) => s.idx), [slides]);
  // How many slides have no thumbnail yet. This is now an ordinary resting
  // state, not a backlog: thumbnails are made as you scroll, so most of a big
  // deck sits here and the menu says "render every slide" rather than implying
  // something went wrong.
  const missingThumbs = useMemo(
    () => blocks.reduce((n, b) => n + b.items.filter((it) => !thumbFor(it.id)).length, 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [blocks, thumbs, warmed],
  );
  // Slides the server has actually given up on — the only count that means
  // something is WRONG, and the one the retry item is named for.
  const failedCount = useMemo(
    () => blocks.reduce((n, b) => n + b.items.filter((it) => failed.has(it.id)).length, 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [blocks, givenUp, warmStatus],
  );
  const runs = useMemo(() => {
    // Contiguous same-section blocks, each carrying its DECK-space index —
    // the grid renders a <ul> per run but drag indices stay deck-wide.
    type Run = { key: string; blocks: { block: SlideBlock; gIdx: number }[] };
    const out: Run[] = [];
    let cur: Run | null = null;
    blocks.forEach((b, gIdx) => {
      if (!cur || cur.key !== b.section) {
        cur = { key: b.section, blocks: [] };
        out.push(cur);
      }
      cur.blocks.push({ block: b, gIdx });
    });
    return out;
  }, [blocks]);

  const q = query.trim().toLowerCase();
  const matches = (b: SlideBlock) =>
    !q ||
    b.items.some((s) =>
      String(s.title || '')
        .toLowerCase()
        .includes(q),
    );
  const matchCount = q ? blocks.filter(matches).length : blocks.length;

  // ----- opening a slide ---------------------------------------------------

  // The one gesture that leaves the overview: double-click, Enter, or the
  // selection bar's primary action.
  const openSlide = useCallback(
    (idx: number) => {
      onSelect(idx);
      onClose();
    },
    [onSelect, onClose],
  );

  // ----- selection ---------------------------------------------------------

  function selectOnly(baseId: string) {
    setSelected(new Set([baseId]));
    setAnchorId(baseId);
  }

  function toggleSelected(baseId: string) {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(baseId)) next.delete(baseId);
      else next.add(baseId);
      return next;
    });
    setAnchorId(baseId);
  }

  // Shift-click takes the run between the anchor and the clicked tile in DECK
  // order — never selection order — and replaces the selection with it.
  function extendTo(baseId: string) {
    const from = blocks.findIndex((b) => b.baseId === (anchorId ?? baseId));
    const to = blocks.findIndex((b) => b.baseId === baseId);
    if (from < 0 || to < 0) return selectOnly(baseId);
    const [lo, hi] = from <= to ? [from, to] : [to, from];
    setSelected(new Set(blocks.slice(lo, hi + 1).map((b) => b.baseId)));
    // The anchor deliberately stays put, so a second Shift-click re-extends
    // from the same pivot rather than walking away from it.
  }

  const clearSelection = useCallback(() => {
    setSelected(new Set());
    setAnchorId(null);
    setMergeOpen(false);
  }, []);

  /** The selection as slides, in deck order — what every action operates on. */
  const selectedBlocks = useMemo(() => blocks.filter((b) => selected.has(b.baseId)), [blocks, selected]);
  function selectedSlides() {
    return selectedBlocks.flatMap((b) => b.items);
  }

  // "Select all" respects the search box: on a filtered view it means "all
  // matches". Dividers are never picked up — nothing acts on them.
  const selectableBlocks = useMemo(
    () => blocks.filter((b) => matches(b) && !b.items.every(isDivider)),
    [blocks, q], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const allSelected = selectableBlocks.length > 0 && selectableBlocks.every((b) => selected.has(b.baseId));

  const toggleSelectAll = useCallback(() => {
    if (allSelected) return clearSelection();
    setSelected(new Set(selectableBlocks.map((b) => b.baseId)));
  }, [allSelected, selectableBlocks, clearSelection]);

  const selectedCount = selected.size;
  const savableCount = selectedSlides().filter((s) => !isDivider(s)).length;
  const soleBlock = selectedBlocks.length === 1 ? selectedBlocks[0] : null;
  // Named here rather than reached for inside a click handler: `items[0]` is
  // an index access, so the guard has to live where the checker can see it.
  const soleSlide = soleBlock?.items[0] ?? null;

  // ----- keyboard ----------------------------------------------------------

  // Arrow keys walk the tiles. Left/right follow deck order; up/down measure
  // the rendered rows (the grid is auto-fill, so the column count is a layout
  // fact, not something this component can know). Shift extends the selection
  // as it goes, like a file grid.
  const moveFocus = useCallback(
    (dir: Direction, fromId: string, extend: boolean) => {
      const root = scrollRef.current;
      if (!root) return;
      const nodes = Array.from(root.querySelectorAll<HTMLElement>('.deck-overview__tile[data-baseid]'));
      const at = nodes.findIndex((n) => n.dataset.baseid === fromId);
      const from = nodes[at];
      if (at < 0 || !from) return;
      let target: HTMLElement | undefined;
      if (dir === 'left') target = nodes[at - 1];
      else if (dir === 'right') target = nodes[at + 1];
      else {
        const here = from.getBoundingClientRect();
        const up = dir === 'up';
        // Measure once and carry the rect alongside its node, rather than
        // index-matching two parallel arrays — the filters below drop entries,
        // so the pairing is what has to survive, not the positions.
        const measured = nodes.map((n) => ({ n, r: n.getBoundingClientRect() }));
        // Candidates on any row above/below, then the nearest such row, then the
        // tile in it whose centre is closest horizontally.
        const cand = measured.filter(({ r }) => (up ? r.bottom <= here.top + 1 : r.top >= here.bottom - 1));
        if (cand.length) {
          const edges = cand.map(({ r }) => (up ? r.bottom : r.top));
          const edge = up ? Math.max(...edges) : Math.min(...edges);
          const row = cand.filter(({ r }) => Math.abs((up ? r.bottom : r.top) - edge) < 2);
          const mid = (here.left + here.right) / 2;
          let best: { n: HTMLElement; d: number } | null = null;
          for (const { n, r } of row) {
            const d = Math.abs((r.left + r.right) / 2 - mid);
            if (!best || d < best.d) best = { n, d };
          }
          target = best?.n;
        }
      }
      const id = target?.dataset.baseid;
      if (!target || !id) return;
      target.focus();
      if (extend) extendTo(id);
      else selectOnly(id);
    },
    // extendTo/selectOnly are redeclared every render but only close over
    // anchorId + blocks, which ARE listed — adding them would rebuild this on
    // every render for no behaviour change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [anchorId, blocks],
  );

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const typing = ['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName ?? '');
      if (e.key === 'a' && (e.metaKey || e.ctrlKey)) {
        // Never steal Cmd+A from the search box or a rename input.
        if (typing) return;
        e.preventDefault();
        toggleSelectAll();
        return;
      }
      // +/−/0 size the grid, the same keys the slide-zoom modal uses for its
      // own zoom. That modal opens ON TOP of this one and does not stop
      // propagation, so bail while it is open or one keypress would do both.
      if (!typing && !zoomSlide && ['+', '=', '-', '_', '0'].includes(e.key)) {
        e.preventDefault();
        const delta = e.key === '+' || e.key === '=' ? 25 : e.key === '0' ? 0 : -25;
        setTileZoom((z) => (delta === 0 ? 100 : Math.min(300, Math.max(75, z + delta))));
        return;
      }
      if (e.key !== 'Escape') return;
      // Mid-drag Escape belongs to dnd-kit (cancel the drag), not the modal.
      if (activeId) return;
      if (confirmingDelete) return setConfirmingDelete(false);
      if (mergeOpen) return setMergeOpen(false);
      if (hasSelection) return clearSelection();
      onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // zoomSlide MUST be a dep: without it the listener keeps the closure from
    // the render before the slide modal opened, and a `+` there sized the grid
    // behind it as well as zooming the slide.
  }, [
    onClose,
    activeId,
    mergeOpen,
    hasSelection,
    confirmingDelete,
    clearSelection,
    toggleSelectAll,
    zoomSlide,
    setTileZoom,
  ]);

  // ----- drag-to-reorder (dnd-kit) -----------------------------------------

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // The preview iframe sits under this modal — it must not eat the mouseup.
  useIframeDragGuard(activeId);

  const dragFromIdx = activeId ? blocks.findIndex((b) => b.baseId === activeId) : -1;
  const dragToIdx = overId ? blocks.findIndex((b) => b.baseId === overId) : -1;

  // Finder rule (mirrors the filmstrip): dragging a selected tile drags the
  // whole selection; dragging an unselected one drags just it.
  const dragIdSet = useMemo<Set<string>>(() => {
    if (!activeId) return new Set();
    if (!selected.has(activeId) || !onReorderMulti) return new Set([activeId]);
    return new Set(blocks.filter((b) => selected.has(b.baseId)).map((b) => b.baseId));
  }, [activeId, selected, blocks, onReorderMulti]);

  function handleDragEnd({ active, over }: DragEndEvent) {
    const ids = dragIdSet;
    setActiveId(null);
    setOverId(null);
    if (!over) return;
    const fromIdx = blocks.findIndex((b) => b.baseId === String(active.id));
    const toIdx = blocks.findIndex((b) => b.baseId === String(over.id));
    if (ids.size > 1) {
      const move = resolveMultiReorder(blocks, ids, fromIdx, toIdx);
      if (move) onReorderMulti?.(move.newList, move.moved, move.toSection, move.sectionMoves);
      return;
    }
    const move = resolveReorder(blocks, fromIdx, toIdx);
    // A block always holds at least one slide; the guard is what makes that
    // readable rather than a case that happens.
    const first = move?.moved.items[0];
    if (move && first) onReorder?.(move.newList, first, move.toSection);
  }

  // ----- verbs -------------------------------------------------------------

  // Delete asks first — one click from the bar would otherwise permanently
  // remove N slides (parity with the filmstrip's per-slide confirm).
  function requestDelete() {
    if (selectedSlides().length === 0) return;
    setConfirmingDelete(true);
  }

  function confirmDelete() {
    setConfirmingDelete(false);
    const toDelete = selectedSlides();
    if (toDelete.length === 0) return;
    onDeleteSlides?.(toDelete);
    clearSelection();
  }

  function confirmSave() {
    // Dividers are structural filler the library refuses — drop them here so
    // the count in the modal matches what actually lands.
    const toSave = selectedSlides().filter((s) => !isDivider(s));
    if (toSave.length === 0) return;
    onSaveSlides?.(toSave);
    clearSelection();
  }

  // A multi:N block is one JSX file rendered N times, so it can't be folded
  // into another slide — the same reason the ⋮ menu hides Split for it.
  const mergeable = (block: SlideBlock) => block.items.length === 1;

  // Selected slides in DECK order, never selection order: the earliest is the
  // one that survives, so the order the user happened to tick must not matter.
  const mergeBlocks = useMemo(() => selectedBlocks.filter(mergeable), [selectedBlocks]);
  const mergeKeep = mergeBlocks[0] || null;
  const mergeSections = useMemo(() => new Set(mergeBlocks.map((b) => b.section)), [mergeBlocks]);
  const canMerge = mergeBlocks.length >= 2;

  function confirmMerge() {
    if (!canMerge || !onMergeSlides) return;
    onMergeSlides({
      // `mergeable` is `items.length === 1`, so every block here has exactly
      // one slide; the filter is what states that to the checker.
      slides: mergeBlocks.map((b) => b.items[0]).filter((s): s is SlideEntry => !!s),
      level: mergeLevel,
      instructions: mergeNotes.trim(),
      model: modelChoice,
    });
    clearSelection();
  }

  const splitTarget = soleBlock && mergeable(soleBlock) ? soleBlock.items[0] : null;

  return (
    <Modal
      ariaLabel={`All slides in ${slug}`}
      onClose={onClose}
      className="modal--overview"
      // One var on the root; every section's grid inherits it, so the whole
      // overview resizes together rather than per-section.
      style={cssVars({ '--ov-tile': `${Math.round(OV_TILE_BASE * (tileZoom / 100))}px` })}
      // Escape is a LADDER here — cancel a drag, then a confirm, then the merge
      // panel, then the selection, and only then close. Modal cannot see any of
      // those, so this dialog keeps the key.
      escape="none"
    >
      <header className="deck-overview__bar">
        <div className="deck-overview__title">
          <strong>All slides</strong>
          <span className="muted small">{q ? `${matchCount} of ${blocks.length}` : `${blocks.length} slides`}</span>
        </div>

        {hasSelection ? (
          <div className="deck-overview__selbar">
            <span className="muted small">{selectedSlides().length} selected</span>
            {soleSlide && (
              <button
                type="button"
                className="btn btn--primary btn--sm"
                onClick={() => openSlide(soleSlide.idx)}
                title="Go to this slide in the editor"
              >
                Open slide {soleSlide.idx + 1} <ArrowRight size={14} />
              </button>
            )}
            {onMergeSlides && (
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => setMergeOpen(true)}
                disabled={busy || !canMerge || mergeOpen}
                title={
                  canMerge
                    ? 'Combine the selected slides into one'
                    : 'Select at least two single-instance slides to merge them'
                }
              >
                <Merge size={14} /> Merge…
              </button>
            )}
            {onSplitSlide && (
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => splitTarget && onSplitSlide(splitTarget)}
                disabled={busy || !splitTarget}
                title={
                  splitTarget
                    ? "Split this slide's content across two slides"
                    : 'Select exactly one single-instance slide to split it'
                }
              >
                Split…
              </button>
            )}
            {onSaveSlides && (
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={confirmSave}
                disabled={savableCount === 0 || busy}
                title="Save the selected slides to the RKH library as one reusable item"
              >
                <LibraryIcon size={14} /> Save to library
              </button>
            )}
            {editable && (
              <button type="button" className="btn btn--sm deck-overview__del" onClick={requestDelete} disabled={busy}>
                <Trash size={14} /> Delete {selectedCount}
              </button>
            )}
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={toggleSelectAll}
              title={q ? 'Select every slide matching the search' : 'Select every slide in the deck'}
            >
              {allSelected ? 'Clear' : 'Select all'}
            </button>
            <button type="button" className="iconbtn" onClick={onClose} aria-label="Close overview" title="Close">
              <X />
            </button>
          </div>
        ) : (
          <>
            <span className="deck-overview__hint muted small">Tick to pick several · double-click to open</span>
            <input
              type="search"
              className="deck-overview__search"
              placeholder="Find a slide by title…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Find a slide by title"
            />
            <ZoomLevelControl value={tileZoom} onChange={setTileZoom} />
            {onInsertSlides && (
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={onInsertSlides}
                title="Add slides to the deck"
              >
                <Plus size={14} /> Insert
              </button>
            )}
            <MoreMenu
              open={moreOpen}
              onToggle={() => setMoreOpen((v) => !v)}
              onClose={() => setMoreOpen(false)}
              regenerating={regenerating}
              missing={missingThumbs}
              failed={failedCount}
              onRun={runWholeDeck}
            />
            <button type="button" className="iconbtn" onClick={onClose} aria-label="Close overview" title="Close">
              <X />
            </button>
          </>
        )}
      </header>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={({ active }) => setActiveId(String(active.id))}
        onDragOver={({ over }) => setOverId(over ? String(over.id) : null)}
        onDragEnd={handleDragEnd}
        onDragCancel={() => {
          setActiveId(null);
          setOverId(null);
        }}
      >
        <SortableContext items={blocks.map((b) => b.baseId)}>
          <div
            className="deck-overview__scroll"
            ref={scrollRef}
            // Clicking the background is how you drop a selection without
            // reaching for Escape — the same as any file grid.
            onMouseDown={(e) => {
              if (e.target instanceof Element && !e.target.closest('.deck-overview__cell, .deck-overview__section'))
                clearSelection();
            }}
          >
            {thumbNote && (
              <p className="muted small deck-overview__thumb-note" role="status">
                {thumbNote}
              </p>
            )}
            {runs.map((run) => {
              const sec = sectionByKey(run.key);
              return (
                <section className="deck-overview__group" key={run.key + '-' + run.blocks[0]?.gIdx}>
                  <SectionHead
                    section={sec}
                    editable={editable}
                    busy={busy}
                    onRecolour={onRecolourSection}
                    onRename={onRenameSection}
                  />
                  <ul className="deck-overview__grid">
                    {run.blocks.map(({ block, gIdx }) => {
                      // A block always holds at least one slide; the tile
                      // reads several fields off it.
                      const first = block.items[0];
                      const isTarget =
                        dragToIdx === gIdx && dragFromIdx >= 0 && dragFromIdx !== gIdx && !dragIdSet.has(block.baseId);
                      return (
                        <SortableOverviewCell
                          key={block.baseId}
                          block={block}
                          thumb={first ? thumbFor(first.id) : undefined}
                          thumbRef={first ? observe(thumbKey(slug, first.id)) : undefined}
                          accent={sec?.color ?? undefined}
                          working={first ? coming(slug, first.id) : false}
                          stuck={first ? isStuck(slug, first.id) : false}
                          onRetry={() => first && retryThumb(slug, first.id)}
                          isActive={block.items.some((s) => s.idx === activeIdx)}
                          activeTileRef={activeTileRef}
                          dim={Boolean(q) && !matches(block)}
                          isSelected={selected.has(block.baseId)}
                          anySelected={hasSelection}
                          isDragMember={dragIdSet.has(block.baseId)}
                          dropBefore={isTarget && dragFromIdx > gIdx}
                          dropAfter={isTarget && dragFromIdx < gIdx}
                          unmergeable={mergeOpen && !mergeable(block)}
                          editable={editable}
                          busy={busy}
                          onSelectOnly={selectOnly}
                          onToggleSelected={toggleSelected}
                          onExtendTo={extendTo}
                          onOpen={openSlide}
                          onMoveFocus={moveFocus}
                          onZoom={setZoomSlide}
                        />
                      );
                    })}
                  </ul>
                </section>
              );
            })}
          </div>
        </SortableContext>
        <DragOverlay>
          {activeId ? (
            <OverviewDragGhost block={blocks[dragFromIdx]} count={dragIdSet.size} thumbFor={thumbFor} />
          ) : null}
        </DragOverlay>
      </DndContext>

      {zoomSlide && (
        <SlidePreviewModal
          slug={slug}
          slideIdx={zoomSlide.idx}
          title={zoomSlide.title}
          slideIdxs={zoomIdxs}
          titleFor={(i) => (slides || []).find((s) => s.idx === i)?.title}
          onClose={() => setZoomSlide(null)}
        />
      )}
      {confirmingDelete &&
        (() => {
          const n = selectedSlides().length;
          return (
            <ConfirmModal
              title={n === 1 ? 'Delete slide?' : `Delete ${n} slides?`}
              body={`${n === 1 ? 'The selected slide' : `The ${n} selected slides`} will be permanently removed from the deck. This can't be undone.`}
              danger
              confirmLabel="Delete"
              busy={busy}
              onCancel={() => setConfirmingDelete(false)}
              onConfirm={confirmDelete}
            />
          );
        })()}
      {mergeOpen && (
        <MergeBar
          keepSlide={mergeKeep?.items[0] || null}
          count={mergeBlocks.length}
          skipped={selectedBlocks.length - mergeBlocks.length}
          crossSection={mergeSections.size > 1}
          keepSectionName={mergeKeep ? sectionByKey(mergeKeep.section).name || mergeKeep.section : ''}
          level={mergeLevel}
          onLevel={setMergeLevel}
          modelOptions={modelOptions}
          model={modelChoice}
          onModel={pickMergeModel}
          notes={mergeNotes}
          onNotes={setMergeNotes}
          onCancel={() => setMergeOpen(false)}
          onConfirm={confirmMerge}
          canMerge={canMerge}
          busy={busy}
        />
      )}
    </Modal>
  );
}

/** Overflow menu for the rare housekeeping verb, so the idle header stays down
 *  to search + Insert + close. */
function MoreMenu({
  open,
  onToggle,
  onClose,
  regenerating,
  missing = 0,
  failed = 0,
  onRun,
}: {
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  regenerating: boolean;
  missing?: number;
  /** Slides the server has given up on — the menu offers a re-run for them
   *  specifically, which is the only way back on this surface. */
  failed?: number;
  onRun: (mode: WholeDeckRun) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  // The menu is portalled, so it is not inside `ref` — without naming it here a
  // click on one of its own items reads as an outside click.
  useDismissMenu([ref, menuRef], onClose, open);
  return (
    <div className="deck-overview__more" ref={ref}>
      <button
        ref={buttonRef}
        type="button"
        className="iconbtn"
        onClick={onToggle}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="More overview actions"
        title="More"
      >
        <KebabHorizontal size={16} />
      </button>
      {open && (
        <AnchoredPop
          anchor={buttonRef}
          popRef={menuRef}
          align="right"
          gap={6}
          className="deck-overview__menu"
          role="menu"
        >
          {/* Three states exist now — cached, not asked for, and failed — so the
              old single adaptive item can't name them. Failures come first,
              because that is the only one where something is WRONG rather than
              merely unrequested. */}
          {failed > 0 && (
            <button
              type="button"
              role="menuitem"
              className="deck-overview__menuitem"
              onClick={() => onRun('retry')}
              disabled={regenerating}
            >
              {regenerating ? 'Working…' : `Retry ${failed} thumbnail${failed === 1 ? '' : 's'}`}
              <span className="muted small">
                {failed === 1 ? "One slide wouldn't render" : `${failed} slides wouldn't render`}. Try them again.
              </span>
            </button>
          )}
          {missing > 0 && (
            <button
              type="button"
              role="menuitem"
              className="deck-overview__menuitem"
              onClick={() => onRun('retry')}
              disabled={regenerating}
            >
              {regenerating ? 'Working…' : 'Render every slide'}
              <span className="muted small">
                Thumbnails are made as you scroll. This renders the whole deck now ({missing} to go).
              </span>
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            className="deck-overview__menuitem"
            onClick={() => onRun('regenerate')}
            disabled={regenerating}
          >
            {regenerating ? 'Working…' : 'Rebuild thumbnails'}
            <span className="muted small">Wipe and re-render every tile, if one rendered wrong.</span>
          </button>
        </AnchoredPop>
      )}
    </div>
  );
}

/**
 * One grid cell — a manifest row's thumbnail, caption and drag affordances.
 *
 * Click selects, Cmd-click adds, Shift-click extends, double-click (or Enter)
 * opens the slide. As in the filmstrip the whole tile drags (5px threshold, so
 * a click still registers) while the grip is the keyboard drag activator.
 *
 * The tick (top-left) and the grip (top-right) are both SIBLINGS of the tile
 * button, never children: a nested button is invalid, and as siblings their
 * clicks and keyboard activation can't fall through to select/open the tile.
 */
type OverviewCellProps = {
  block: SlideBlock;
  /** Undefined until the server has rendered one — the grey shimmer is what
   *  that absence looks like, not an error. */
  thumb: Thumb | undefined;
  /** Ref callback from the viewport observer: scrolling to this tile IS the
   *  request for its image. */
  thumbRef?: (el: Element | null) => void;
  /** The section's accent token. A waiting tile is tinted with it, so a grid
   *  still rendering reads as THIS deck arriving rather than a wall of grey. */
  accent?: string;
  /** Queued or rendering — what the shimmer keys on. */
  working?: boolean;
  /** The server has given up on this slide; the tile offers a retry. */
  stuck?: boolean;
  onRetry?: () => void;
  isActive: boolean;
  // See Filmstrip.activeRef: RefObject<T>, not RefObject<T | null>.
  activeTileRef: RefObject<HTMLButtonElement>;
  /** Search is on and this block does not match: shown, faded, still there. */
  dim: boolean;
  isSelected: boolean;
  anySelected: boolean;
  isDragMember: boolean;
  dropBefore: boolean;
  dropAfter: boolean;
  /** The merge bar is open and this block is a multi:N, which cannot fold. */
  unmergeable: boolean;
  editable: boolean;
  busy?: boolean;
  onSelectOnly: (baseId: string) => void;
  onToggleSelected: (baseId: string) => void;
  onExtendTo: (baseId: string) => void;
  /** Takes the slide's DECK index, not its id — it goes straight to
   *  `onSelect`, which is index-based everywhere in the editor. */
  onOpen: (idx: number) => void;
  onMoveFocus: (dir: Direction, fromId: string, extend: boolean) => void;
  /** The magnifier hands up the SLIDE, since the zoom modal names it. */
  onZoom?: ((slide: SlideEntry) => void) | null;
};

function SortableOverviewCell({
  block,
  thumb,
  thumbRef,
  /** The section's accent token. A waiting tile is tinted with it, so a grid
   *  that is still rendering reads as THIS deck arriving rather than as a wall
   *  of identical grey. Costs nothing: the colour is already on the client. */
  accent,
  working,
  stuck,
  onRetry,
  isActive,
  activeTileRef,
  dim,
  isSelected,
  anySelected,
  isDragMember,
  dropBefore,
  dropAfter,
  unmergeable,
  editable,
  busy,
  onSelectOnly,
  onToggleSelected,
  onExtendTo,
  onOpen,
  onMoveFocus,
  onZoom,
}: OverviewCellProps) {
  const disabled = !editable || busy;
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, isDragging } = useSortable({
    id: block.baseId,
    disabled,
  });
  const { onKeyDown: _dragKeyDown, ...dragListeners } = listeners || {};

  // Every block holds at least one slide — groupBlocks only creates one when
  // it has a slide to put in it.
  const first = block.items[0];
  const last = block.items[block.items.length - 1];
  const cls =
    'deck-overview__tile' +
    (dim ? ' deck-overview__tile--dim' : '') +
    (isDragging || isDragMember ? ' deck-overview__tile--dragging' : '') +
    (unmergeable ? ' deck-overview__tile--unpickable' : '') +
    (isSelected ? ' deck-overview__tile--selected' : '');

  const ARROWS: Record<string, Direction> = {
    ArrowLeft: 'left',
    ArrowRight: 'right',
    ArrowUp: 'up',
    ArrowDown: 'down',
  };

  if (!first || !last) return null;

  return (
    <li
      ref={setNodeRef}
      className={
        'deck-overview__cell' +
        (dropBefore ? ' deck-overview__cell--drop-before' : '') +
        (dropAfter ? ' deck-overview__cell--drop-after' : '')
      }
    >
      <button
        type="button"
        ref={isActive ? activeTileRef : null}
        data-baseid={block.baseId}
        className={cls}
        // Shift-click would otherwise drag-select the caption text.
        onMouseDown={(e) => {
          if (e.shiftKey) e.preventDefault();
        }}
        onClick={(e) => {
          if (e.shiftKey) return onExtendTo(block.baseId);
          if (e.metaKey || e.ctrlKey) return onToggleSelected(block.baseId);
          onSelectOnly(block.baseId);
        }}
        onDoubleClick={() => onOpen(first.idx)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            return onOpen(first.idx);
          }
          const dir = ARROWS[e.key];
          if (dir) {
            e.preventDefault();
            onMoveFocus(dir, block.baseId, e.shiftKey);
          }
        }}
        aria-pressed={isSelected}
        aria-current={isActive ? 'true' : undefined}
        aria-label={`Slide ${first.idx + 1}: ${first.title || 'Untitled'}${isActive ? ' (current)' : ''} (double-click or press Enter to open)`}
        {...dragListeners}
      >
        {/* `thumbRef` is what asks for this tile's image — scrolling to it IS the
            request. It goes on this inner span rather than the <li>, which
            already carries dnd-kit's own ref. */}
        <span className="deck-overview__shot" ref={thumbRef}>
          {thumb ? (
            <img className="deck-overview__img" src={thumb.url} alt="" loading="lazy" />
          ) : stuck ? (
            // This one failed, or the wait for it ran out. Never leave a dead
            // grey square: the way back has to be on the tile itself, because
            // the deck-wide Refresh is both hidden in a menu and far more work
            // than re-rendering the one slide that went wrong.
            <span
              className="deck-overview__retry"
              role="button"
              tabIndex={0}
              title="This preview didn't render. Try again."
              aria-label={`Retry the preview for slide ${first.idx + 1}`}
              onClick={(e) => {
                e.stopPropagation();
                onRetry?.();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  onRetry?.();
                }
              }}
            >
              <Refresh size={16} />
            </span>
          ) : (
            // No live iframe fallback: N live iframes would hammer the deck's
            // Vite child (and flash on reorder) — the very problem cached thumbs
            // fixed. STILL unless a render is genuinely in flight for this tile:
            // most of a big deck is now deliberately un-rendered, so a shimmer
            // here would promise images that are never coming. The sweep's delay
            // is keyed to slide index (modulo, so long decks wrap within one
            // cycle) so a batch reads as generating in slide order.
            //
            // Tinted with the section's accent (very low alpha, over the black
            // frame): a waiting grid then shows the deck's own structure — where
            // one section ends and the next begins — instead of reading as
            // nothing having happened. It is the cheapest perceived-speed win
            // available here, because the colour is already on the client and no
            // image has to be made for it.
            <span
              className={'deck-overview__skeleton' + (working ? ' is-working' : '')}
              aria-hidden="true"
              style={{
                ...(accent ? { '--tile-accent': colourHex(accent) } : null),
                ...(working ? { animationDelay: `${(first.idx % 12) * 0.09}s` } : null),
              }}
            />
          )}
        </span>
        {/* The current slide's quiet marker. Selection owns the ring, so this
            is a filled chip on the number the tile already carries — the two
            states can sit side by side and still read apart at a glance. */}
        <span className={'deck-overview__num' + (isActive ? ' deck-overview__num--now' : '')}>
          {first.idx + 1}
          {block.items.length > 1 ? `–${last.idx + 1}` : ''}
          {isActive ? <em>now</em> : null}
        </span>
      </button>
      {/* The visible way to build a scattered selection — a plain click on the
          tick does what Cmd-click on the tile does. Shown on hover, and kept on
          every tile while a selection exists. */}
      <button
        type="button"
        className={'deck-overview__check' + (isSelected ? ' deck-overview__check--on' : '')}
        aria-pressed={isSelected}
        aria-label={(isSelected ? 'Deselect' : 'Select') + ' slide ' + (first.idx + 1)}
        title={isSelected ? 'Deselect this slide' : 'Select this slide (add it to the selection)'}
        data-armed={anySelected || undefined}
        // The tile's drag sensor lives on a sibling, but a stray mousedown here
        // must not start a text drag or bubble to the background's clear-all.
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          onToggleSelected(block.baseId);
        }}
      />
      {/* Read this slide properly. A SIBLING for the same reason as the tick:
          the tile is itself a button. The thumbnail is a 640x360 raster, so
          the magnifier opens a live frame instead of scaling this one up. */}
      <button
        type="button"
        className="deck-overview__zoom"
        title="Take a closer look"
        aria-label={`Preview slide ${first.idx + 1}: ${first.title || 'Untitled'}`}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          onZoom?.(first);
        }}
      >
        <ZoomIn size={14} />
      </button>
      {/* Grip is a SIBLING of the button (not a child) so its keyboard
          activation never fires the tile's selection. */}
      {editable && (
        <button
          type="button"
          className="deck-overview__grip"
          ref={setActivatorNodeRef}
          disabled={disabled}
          {...attributes}
          {...listeners}
          title="Drag to reorder, or press Space, then use the arrow keys"
          aria-label={'Reorder slide ' + (first.idx + 1)}
        >
          <Drag size={14} />
        </button>
      )}
      <span className="deck-overview__caption" title={first.title}>
        {first.title || <em className="muted">Untitled</em>}
      </span>
    </li>
  );
}

/** The thumbnail that follows the cursor mid-drag. A multi-drag stacks
 *  offset card shadows behind it and wears a count badge. */
function OverviewDragGhost({
  block,
  count = 1,
  thumbFor,
}: {
  /** Undefined when the drag index finds no block — the overlay renders on
   *  every drag state, including that one. */
  block: SlideBlock | undefined;
  count?: number;
  thumbFor: (id: string) => Thumb | undefined;
}) {
  if (!block) return null;
  const first = block.items[0];
  if (!first) return null;
  const thumb = thumbFor(first.id);
  return (
    <div
      className={
        'deck-overview__tile deck-overview__tile--ghost' + (count > 1 ? ' deck-overview__tile--ghost-stack' : '')
      }
    >
      {thumb ? <img className="deck-overview__img" src={thumb.url} alt="" /> : null}
      <span className="deck-overview__num">{first.idx + 1}</span>
      {count > 1 && (
        <span className="deck-overview__ghost-badge" aria-hidden="true">
          {count}
        </span>
      )}
    </div>
  );
}

/** How freely the AI may edit the words when folding slides together. Values
 *  match the levels the deck system prompt defines; the wording mirrors the
 *  new-deck wizard's VERBATIM_CHIPS so the same idea reads the same way
 *  across the studio. */
const MERGE_LEVELS: { value: MergeLevel; label: string; hint: string }[] = [
  { value: 'strict', label: 'My exact words', hint: 'Verbatim: layout changes only, so it may overflow' },
  { value: 'light', label: 'Light editing', hint: 'Tighten the wording, keep every point' },
  { value: 'condense', label: 'Rewrite to fit', hint: 'Free to cut and summarise so it fits' },
];

/** Footer bar for a merge: what will happen, then the two choices that change
 *  it. Merge confirms from here rather than the selection bar because the
 *  options don't fit a single row. */
type MergeBarProps = {
  /** The slide that SURVIVES — null while fewer than two are picked, which is
   *  the state the summary line describes instead. */
  keepSlide: SlideEntry | null;
  count: number;
  /** Selected blocks a merge cannot take (multi:N), named in the summary. */
  skipped: number;
  crossSection: boolean;
  keepSectionName: string;
  level: MergeLevel;
  onLevel: (level: MergeLevel) => void;
  modelOptions?: ChatModelOption[] | null;
  model: string | null;
  onModel: (value: string) => void;
  notes: string;
  onNotes: (notes: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
  canMerge: boolean;
  busy?: boolean;
};

function MergeBar({
  keepSlide,
  count,
  skipped,
  crossSection,
  keepSectionName,
  level,
  onLevel,
  modelOptions,
  model,
  onModel,
  notes,
  onNotes,
  onCancel,
  onConfirm,
  canMerge,
  busy,
}: MergeBarProps) {
  // The fallback is 'light', the default level — MERGE_LEVELS is a literal, so
  // index 1 is always there, but saying so costs less than an assertion.
  const chosen = MERGE_LEVELS.find((l) => l.value === level) ?? MERGE_LEVELS[1];
  const chosenModel = (modelOptions || []).find((m) => m.value === model) || null;
  return (
    <footer className="deck-overview__mergebar">
      <p className="deck-overview__mergesum">
        {count < 2 || !keepSlide ? (
          <span className="muted">Select at least two slides to merge.</span>
        ) : (
          <>
            <strong>
              {count} slides merge into slide {keepSlide.idx + 1}
            </strong>
            <span className="muted">
              {' '}
              ({keepSlide.title || 'Untitled'}). The others are deleted.
              {crossSection ? ` They span several sections, so the merged slide stays in "${keepSectionName}".` : ''}
              {skipped > 0
                ? ` ${skipped === 1 ? 'One selected slide is' : `${skipped} selected slides are`} used several times in the deck, so ${skipped === 1 ? 'it stays' : 'they stay'} as ${skipped === 1 ? 'it is' : 'they are'}.`
                : ''}
            </span>
          </>
        )}
      </p>

      <div className="deck-overview__mergerow">
        <span className="deck-overview__mergelabel">Content</span>
        <div className="deck-overview__chips">
          {MERGE_LEVELS.map((l) => (
            <button
              key={l.value}
              type="button"
              className={'chip' + (level === l.value ? ' chip--active' : '')}
              onClick={() => onLevel(l.value)}
              aria-pressed={level === l.value}
              title={l.hint}
            >
              {l.label}
            </button>
          ))}
        </div>
      </div>
      <p className="deck-overview__mergehint muted small">{chosen?.hint}</p>

      {(modelOptions || []).length > 0 && (
        <>
          <div className="deck-overview__mergerow">
            <span className="deck-overview__mergelabel">Model</span>
            <div className="deck-overview__chips">
              {(modelOptions || []).map((m) => (
                <button
                  key={m.value}
                  type="button"
                  className={'chip' + (model === m.value ? ' chip--active' : '')}
                  onClick={() => onModel(m.value)}
                  aria-pressed={model === m.value}
                  title={m.hint}
                >
                  {m.label}
                  {m.detail ? ` · ${m.detail}` : ''}
                </button>
              ))}
            </div>
          </div>
          <p className="deck-overview__mergehint muted small">
            {chosenModel?.value === STRUCTURAL_MODEL
              ? 'A merge deletes the other slides, so the strongest model is the safe starting point.' +
                // Only worth saying while there IS a lighter Claude to move to.
                ((modelOptions || []).some((m) => m.value === 'sonnet') ? ' Two short slides are fine on Sonnet.' : '')
              : chosenModel?.hint}
          </p>
        </>
      )}

      <div className="deck-overview__mergerow">
        <label className="deck-overview__mergelabel" htmlFor="merge-notes">
          Anything else?
        </label>
        <input
          id="merge-notes"
          type="text"
          className="modal__input"
          placeholder="Optional: e.g. use a two-column layout"
          value={notes}
          onChange={(e) => onNotes(e.target.value)}
          maxLength={300}
        />
      </div>

      <div className="deck-overview__mergeactions">
        <button type="button" className="btn btn--ghost btn--sm" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="btn btn--primary btn--sm" onClick={onConfirm} disabled={!canMerge || busy}>
          <Merge size={14} /> Merge{canMerge ? ` ${count} slides` : ''}
        </button>
      </div>
    </footer>
  );
}

/** A section heading with an in-place colour swatch popover and double-click
 *  rename — the same affordances as the filmstrip's SectionHeader, sized for
 *  the overview. Read-only (dot + name only) when the deck isn't editable. */
function SectionHead({
  section,
  editable,
  busy,
  onRecolour,
  onRename,
}: {
  section: SectionRef;
  editable: boolean;
  busy?: boolean;
  onRecolour?: ((section: SectionRef, token: string) => void) | null;
  onRename?: ((section: SectionRef, name: string) => void) | null;
}) {
  const [popover, setPopover] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(section.name || section.key);
  const rootRef = useRef<HTMLHeadingElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) {
      setDraft(section.name || section.key);
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 0);
    }
  }, [editing]); // eslint-disable-line react-hooks/exhaustive-deps

  const dotRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const closePopover = useCallback(() => setPopover(false), []);
  // The swatch panel is portalled, so it sits outside rootRef — it has to be
  // named here or picking a colour dismisses the panel picking it.
  useDismissMenu([rootRef, popRef], closePopover, !!popover);

  function commitRename() {
    setEditing(false);
    const name = draft.trim();
    if (name && name !== section.name) onRename?.(section, name);
  }

  const hex = colourHex(section.color || 'ORANGE');

  return (
    <h3 className="deck-overview__section" ref={rootRef}>
      {editable ? (
        <button
          ref={dotRef}
          type="button"
          className="deck-overview__dot deck-overview__dot--btn"
          style={{ background: hex }}
          onClick={() => setPopover((v) => !v)}
          title="Change the section colour"
          aria-label={'Section colour: ' + (section.color || 'ORANGE').toLowerCase()}
          disabled={busy}
        />
      ) : (
        section.color && <span className="deck-overview__dot" style={{ background: hex }} aria-hidden="true" />
      )}
      {editing ? (
        <input
          ref={inputRef}
          className="deck-overview__section-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitRename}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitRename();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              setEditing(false);
            }
          }}
          maxLength={80}
        />
      ) : (
        <span
          className={'deck-overview__section-name' + (editable ? ' deck-overview__section-name--editable' : '')}
          onDoubleClick={editable ? () => setEditing(true) : undefined}
          title={editable ? (section.name || section.key) + ' (double-click to rename)' : undefined}
        >
          {section.name || section.key}
        </span>
      )}
      {popover && (
        /* Section headings run the length of a long overview, so this one can
           open anywhere in the scroller — placed against the viewport it flips
           above the dot near the bottom rather than opening into the fold. */
        <AnchoredPop anchor={dotRef} popRef={popRef} align="left" gap={5} className="deck-overview__pop">
          <AccentSwatches
            value={section.color}
            onPick={(token, meta) => {
              // A custom pick keeps the popover open (see SectionHeader).
              if (!meta?.custom) setPopover(false);
              if (token !== section.color) onRecolour?.(section, token);
            }}
            disabled={busy}
          />
        </AnchoredPop>
      )}
    </h3>
  );
}
